import type { YdymImageChain } from './qrDetect';

const TRON_RE = /T[1-9A-HJ-NP-Za-km-z]{33}/;
const ETH_RE = /0x[a-fA-F0-9]{40}/i;
const BTC_RE = /bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{11,87}/i;
const BASE58_BODY = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const GLYPHS = `T0x${BASE58_BODY}`;
const TW = 12;
const TH = 18;

let templates: Map<string, Uint8Array> | undefined;

export function isAddressStripGeometry(width: number, height: number): boolean {
  const narrow = Math.min(width, height);
  const longer = Math.max(width, height);
  if (narrow < 8 || longer < 80) return false;
  return longer / narrow >= 1.5;
}

function makeCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas | undefined {
  try {
    if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height);
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      return canvas;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function getTemplates(): Map<string, Uint8Array> {
  if (templates) return templates;
  templates = new Map();
  const canvas = makeCanvas(TW, TH);
  if (!canvas) return templates;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
  if (!ctx) return templates;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  const fonts = [
    `bold ${TH - 2}px "Segoe UI", Roboto, Arial, sans-serif`,
    `bold ${TH - 2}px "Courier New", ui-monospace, monospace`,
  ];
  for (const ch of GLYPHS) {
    const acc = new Uint16Array(TW * TH);
    let copies = 0;
    for (const font of fonts) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, TW, TH);
      ctx.fillStyle = '#000';
      ctx.font = font;
      ctx.fillText(ch, TW / 2, TH / 2 + 1);
      const img = ctx.getImageData(0, 0, TW, TH);
      for (let i = 0, p = 0; i < img.data.length; i += 4, p++) {
        acc[p] += img.data[i] < 140 ? 1 : 0;
      }
      copies += 1;
    }
    const bin = new Uint8Array(TW * TH);
    const half = copies / 2;
    for (let p = 0; p < bin.length; p++) bin[p] = acc[p] >= half ? 1 : 0;
    templates.set(ch, bin);
  }
  return templates;
}

function toGray(src: ImageData): Uint8Array {
  const n = src.width * src.height;
  const gray = new Uint8Array(n);
  const s = src.data;
  for (let i = 0, p = 0; i < s.length; i += 4, p++) {
    gray[p] = (s[i] * 0.299 + s[i + 1] * 0.587 + s[i + 2] * 0.114) | 0;
  }
  return gray;
}

function otsuThreshold(gray: Uint8Array): number {
  const hist = new Float64Array(256);
  for (let i = 0; i < gray.length; i++) hist[gray[i]] += 1;
  const total = gray.length || 1;
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let best = 0;
  let bestT = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (!wB) continue;
    const wF = total - wB;
    if (!wF) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best) {
      best = between;
      bestT = t;
    }
  }
  return bestT;
}

function cropRect(src: ImageData, left: number, top: number, right: number, bottom: number): ImageData | undefined {
  const w = Math.max(1, right - left + 1);
  const h = Math.max(1, bottom - top + 1);
  if (w < 24 || h < 8) return undefined;
  const out = new ImageData(w, h);
  for (let y = 0; y < h; y++) {
    const srcOff = ((top + y) * src.width + left) * 4;
    out.data.set(src.data.subarray(srcOff, srcOff + w * 4), y * w * 4);
  }
  return out;
}

/** Isolate the light chat-bubble (or white address bar) from a dark wallpaper. */
function cropLightBubble(src: ImageData): ImageData | undefined {
  const { width: w, height: h } = src;
  const gray = toGray(src);
  const hist = new Uint32Array(256);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  let acc = 0;
  let lightThr = 170;
  const cutoff = gray.length * 0.62;
  for (let v = 0; v < 256; v++) {
    acc += hist[v];
    if (acc >= cutoff) {
      lightThr = Math.min(210, Math.max(140, v + 6));
      break;
    }
  }
  const rowLight = new Int32Array(h);
  const colLight = new Int32Array(w);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (gray[y * w + x] >= lightThr) {
        rowLight[y]++;
        colLight[x]++;
      }
    }
  }
  const rowNeed = Math.max(4, Math.round(w * 0.06));
  const colNeed = Math.max(3, Math.round(h * 0.18));
  let top = 0;
  let bot = h - 1;
  let left = 0;
  let right = w - 1;
  while (top < h && rowLight[top] < rowNeed) top++;
  while (bot > top && rowLight[bot] < rowNeed) bot--;
  while (left < w && colLight[left] < colNeed) left++;
  while (right > left && colLight[right] < colNeed) right--;
  const padX = Math.max(1, Math.round((right - left) * 0.01));
  const padY = Math.max(1, Math.round((bot - top) * 0.08));
  top = Math.max(0, top - padY);
  bot = Math.min(h - 1, bot + padY);
  left = Math.max(0, left - padX);
  right = Math.min(w - 1, right + padX);
  const bw = right - left + 1;
  if (bw >= 80) {
    // Drop timestamp / read-receipts on the right of a Telegram bubble.
    right = left + Math.max(48, Math.floor(bw * 0.88));
  }
  return cropRect(src, left, top, right, bot);
}

function binarizeGray(gray: Uint8Array, width: number, height: number, invert: boolean): Uint8Array {
  const thr = otsuThreshold(gray);
  const out = new Uint8Array(width * height);
  for (let p = 0; p < gray.length; p++) {
    const ink = gray[p] < thr;
    out[p] = invert ? (ink ? 0 : 1) : (ink ? 1 : 0);
  }
  return out;
}

function inkRatio(bin: Uint8Array): number {
  let c = 0;
  for (let i = 0; i < bin.length; i++) c += bin[i];
  return bin.length ? c / bin.length : 0;
}

function cropBand(bin: Uint8Array, width: number, height: number): {
  data: Uint8Array; width: number; height: number;
} | undefined {
  const rowInk = new Int32Array(height);
  for (let y = 0; y < height; y++) {
    let c = 0;
    const off = y * width;
    for (let x = 0; x < width; x++) c += bin[off + x];
    rowInk[y] = c;
  }
  const rowThr = Math.max(2, width * 0.015);
  let top = 0;
  let bottom = height - 1;
  while (top < height && rowInk[top] < rowThr) top++;
  while (bottom > top && rowInk[bottom] < rowThr) bottom--;
  if (bottom - top < 6) return undefined;
  const pad = Math.max(1, Math.round((bottom - top) * 0.1));
  top = Math.max(0, top - pad);
  bottom = Math.min(height - 1, bottom + pad);
  const hh = bottom - top + 1;
  const out = new Uint8Array(width * hh);
  for (let y = 0; y < hh; y++) {
    out.set(bin.subarray((top + y) * width, (top + y) * width + width), y * width);
  }
  return { data: out, width, height: hh };
}

function matchGlyph(patch: Uint8Array): { ch: string; score: number } {
  const map = getTemplates();
  let best = '?';
  let bestScore = Infinity;
  map.forEach((tpl, ch) => {
    let err = 0;
    for (let i = 0; i < tpl.length; i++) err += tpl[i] ^ patch[i];
    if (err < bestScore) {
      bestScore = err;
      best = ch;
    }
  });
  return { ch: best, score: bestScore };
}

function scalePatch(src: Uint8Array, sw: number, sh: number, x0: number, y0: number, gw: number, gh: number): Uint8Array {
  const out = new Uint8Array(TW * TH);
  for (let y = 0; y < TH; y++) {
    const sy = y0 + Math.min(gh - 1, Math.floor((y * gh) / TH));
    for (let x = 0; x < TW; x++) {
      const sx = x0 + Math.min(gw - 1, Math.floor((x * gw) / TW));
      out[y * TW + x] = src[sy * sw + sx];
    }
  }
  return out;
}

function readLine(bin: Uint8Array, width: number, height: number): string {
  const colInk = new Int32Array(width);
  for (let x = 0; x < width; x++) {
    let c = 0;
    for (let y = 0; y < height; y++) c += bin[y * width + x];
    colInk[x] = c;
  }
  const colThr = Math.max(1, height * 0.1);
  const chars: string[] = [];
  let x = 0;
  while (x < width) {
    while (x < width && colInk[x] < colThr) x++;
    if (x >= width) break;
    const x0 = x;
    while (x < width && colInk[x] >= colThr) x++;
    const gw = x - x0;
    if (gw < 1 || gw > Math.max(24, width * 0.2)) continue;
    const patch = scalePatch(bin, width, height, x0, 0, gw, height);
    const hit = matchGlyph(patch);
    if (hit.score <= TW * TH * 0.48) chars.push(hit.ch);
  }
  return chars.join('');
}

function collapse(s: string): string {
  return String(s || '').replace(/[\s\u200B\uFEFF\u00a0\u3000]+/g, '');
}

function gappedTrc20(s: string): string | undefined {
  const cleaned = collapse(s);
  if (cleaned.length < 34) return undefined;
  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned[i] !== 'T' && cleaned[i] !== 't') continue;
    let body = '';
    for (let j = i + 1; j < cleaned.length && body.length < 48; j++) {
      const c = cleaned[j];
      if (BASE58_BODY.includes(c)) body += c;
      else if (c === '0') body += 'o';
      else if (c === 'l' || c === 'I') body += '1';
    }
    if (body.length >= 33) {
      const addr = `T${body.slice(0, 33)}`;
      if (TRON_RE.test(addr)) return addr;
    }
  }
  return undefined;
}

export function chainFromOcrText(raw: string): YdymImageChain | undefined {
  if (!raw) return undefined;
  const collapsed = collapse(raw);
  const alnum = collapsed.replace(/[^0-9A-Za-z]/g, '');
  const candidates = [raw, collapsed, alnum];
  for (const c of candidates) {
    if (TRON_RE.test(c) || gappedTrc20(c)) return 'TRC20';
    if (ETH_RE.test(c)) return 'ETH';
    if (BTC_RE.test(c)) return 'BTC';
  }
  return undefined;
}

async function detectWithTextDetector(bitmap: ImageBitmap): Promise<string> {
  const Detector = (globalThis as {
    TextDetector?: new () => { detect: (input: ImageBitmap) => Promise<Array<{ rawValue?: string }>> };
  }).TextDetector;
  if (!Detector) return '';
  try {
    const hits = await new Detector().detect(bitmap);
    return (hits || []).map((h) => String(h.rawValue || '')).join(' ');
  } catch {
    return '';
  }
}

function bitmapToImageData(bitmap: ImageBitmap, maxSide: number): ImageData | undefined {
  let w = bitmap.width;
  let h = bitmap.height;
  if (w < 8 || h < 8) return undefined;
  const max = Math.max(w, h);
  const scale = max > maxSide ? maxSide / max : 1;
  const nw = Math.max(1, Math.round(w * scale));
  const nh = Math.max(1, Math.round(h * scale));
  const canvas = makeCanvas(nw, nh);
  if (!canvas) return undefined;
  const ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
  if (!ctx) return undefined;
  ctx.imageSmoothingEnabled = scale < 1;
  ctx.drawImage(bitmap, 0, 0, nw, nh);
  return ctx.getImageData(0, 0, nw, nh);
}

function scaleImageData(src: ImageData, factor: number): ImageData | undefined {
  const nw = Math.max(1, Math.round(src.width * factor));
  const nh = Math.max(1, Math.round(src.height * factor));
  const canvas = makeCanvas(nw, nh);
  const tmp = makeCanvas(src.width, src.height);
  if (!canvas || !tmp) return undefined;
  const tctx = tmp.getContext('2d') as CanvasRenderingContext2D | null;
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
  if (!tctx || !ctx) return undefined;
  tctx.putImageData(src, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tmp as CanvasImageSource, 0, 0, nw, nh);
  return ctx.getImageData(0, 0, nw, nh);
}

function templateOcrOn(image: ImageData): string {
  const gray = toGray(image);
  const texts: string[] = [];
  for (const invert of [false, true]) {
    const bin = binarizeGray(gray, image.width, image.height, invert);
    const ratio = inkRatio(bin);
    if (ratio < 0.02 || ratio > 0.72) continue;
    const band = cropBand(bin, image.width, image.height);
    if (!band) continue;
    const line = readLine(band.data, band.width, band.height);
    if (line) texts.push(line);
  }
  return texts.sort((a, b) => b.length - a.length)[0] || '';
}

function collectOcrTexts(frame: ImageData): string[] {
  const out: string[] = [];
  const bubble = cropLightBubble(frame);
  const crops = [bubble, frame].filter(Boolean) as ImageData[];
  for (const crop of crops) {
    let img = crop;
    if (img.height > 0 && img.height < 28) {
      const up = scaleImageData(img, Math.min(4, 32 / img.height));
      if (up) img = up;
    }
    const line = templateOcrOn(img);
    if (line) out.push(line);
  }
  return out;
}

export async function detectWalletChainFromOcr(bitmap: ImageBitmap): Promise<{
  chain?: YdymImageChain;
  text: string;
}> {
  const native = await detectWithTextDetector(bitmap);
  let text = native;
  let chain = chainFromOcrText(text);
  if (chain) return { chain, text };

  const sides = [1400, 900, 560];
  for (const side of sides) {
    const frame = bitmapToImageData(bitmap, side);
    if (!frame) continue;
    const lines = collectOcrTexts(frame);
    for (const line of lines) {
      if (line.length > text.length) text = line;
      chain = chainFromOcrText(line);
      if (chain) return { chain, text: line };
    }
  }
  return { chain, text };
}
